import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import type { PublicScheduleEvent } from '../types/api/publicSchedule.types';
import type {
  PublicScheduleTableRow,
  PublicScheduleTableConfig,
} from '../types/common/publicSchedule.types';
import { mapEventsToRows } from '../ViewerApp/PublicSchedulePage/utils/mapEventsToRows';
import {
  renderTeamLogoCell,
  renderScoreCell,
  renderLocationCell,
} from '../ViewerApp/PublicSchedulePage/grid/cellRenderers';

/** Optional hidden column fields. */
const HIDDEN_COLUMN_FIELDS = ['matchDay'];

/** Home/Away column width, viewport-aware: narrower on mobile so both fit onscreen. */
const HOME_AWAY_WIDTH = {
  desktop: { minWidth: 180, maxWidth: 300 },
  mobile: { minWidth: 120, maxWidth: 150 },
};

/** Home/Away width when autosizing: low floor so content decides, cap so long names don't overflow. */
const HOME_AWAY_AUTOSIZE_WIDTH = {
  desktop: { minWidth: 80, maxWidth: 300 },
  mobile: { minWidth: 80, maxWidth: 150 },
};

export interface ScheduleTableConfigOptions {
  /** Set when the grid uses the `autosize` prop: Home/Away size to their contents. */
  autosize?: boolean;
}

/**
 * Custom hook for public schedule table configuration
 * Returns column definitions and row data for the public schedule table
 * Reusable across different features (Public Schedule, Public Standing, etc.)
 *
 * @param events - Optional array of public schedule events from the API
 * @param options - Column sizing options, see ScheduleTableConfigOptions
 * @returns Object with columns and rows for the DataGrid
 */
export const useScheduleTableConfig = (
  scheduleData?: Array<PublicScheduleEvent>,
  options: ScheduleTableConfigOptions = {}
): PublicScheduleTableConfig => {
  const autosize = options.autosize ?? false;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Based on reference implementation from kitman-frontend FixtureScheduleView
  const getScheduleColumns = (): Array<GridColDef> => {
    const widthPreset = autosize ? HOME_AWAY_AUTOSIZE_WIDTH : HOME_AWAY_WIDTH;
    const teamColumnBounds = isMobile
      ? widthPreset.mobile
      : widthPreset.desktop;
    // Autosize zeroes flex anyway; omitting it avoids a full-width flash.
    const teamColumnFlex = autosize ? {} : { flex: 1 };

    return [
      // Home Team Name
      {
        field: 'homeTeam',
        headerName: 'Home',
        ...teamColumnFlex,
        ...teamColumnBounds,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
      },
      // Home Team Logo - Empty header column for logo display
      {
        field: 'homeTeamLogo',
        headerName: '',
        width: 40,
        sortable: false,
        renderCell: (params) => renderTeamLogoCell(params, params.row.homeTeam),
      },
      // Home Squad
      {
        field: 'homeSquad',
        headerName: 'Squad',
        minWidth: 100,
        maxWidth: 150,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
      },
      // Score
      {
        field: 'score',
        headerName: 'Score',
        minWidth: 80,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: renderScoreCell,
      },
      // Away Squad
      {
        field: 'awaySquad',
        headerName: 'Squad',
        minWidth: 100,
        maxWidth: 150,
        sortable: false,
      },
      // Away Team Logo - Empty header column for logo display
      {
        field: 'awayTeamLogo',
        headerName: '',
        width: 40,
        sortable: false,
        renderCell: (params) => renderTeamLogoCell(params, params.row.awayTeam),
      },
      // Away Team Name
      {
        field: 'awayTeam',
        headerName: 'Away',
        ...teamColumnFlex,
        ...teamColumnBounds,
        sortable: false,
      },
      // Match ID
      {
        field: 'matchId',
        headerName: 'Match ID',
        minWidth: 150,
        maxWidth: 220,
        sortable: false,
      },
      // Match Day
      {
        field: 'matchDay',
        headerName: 'Match Day',
        minWidth: 120,
        maxWidth: 280,
        sortable: false,
      },
      // Competition
      {
        field: 'competition',
        headerName: 'Competition',
        minWidth: 140,
        maxWidth: 250,
        sortable: false,
      },
      // Date
      {
        field: 'date',
        headerName: 'Date',
        minWidth: 120,
        maxWidth: 150,
        sortable: false,
      },
      // Time
      {
        field: 'time',
        headerName: 'Time',
        minWidth: 120,
        maxWidth: 150,
        sortable: false,
      },
      // Location
      {
        field: 'location',
        headerName: 'Location / Pitch',
        flex: 1,
        minWidth: 250,
        maxWidth: 320,
        sortable: false,
        renderCell: renderLocationCell,
      },
    ];
  };

  const columns = useMemo(
    () =>
      getScheduleColumns().filter(
        (column) => !HIDDEN_COLUMN_FIELDS.includes(column.field)
      ),
    [isMobile, autosize]
  );

  const rows: Array<PublicScheduleTableRow> = useMemo(
    () => mapEventsToRows(scheduleData ?? []),
    [scheduleData]
  );

  return { columns, rows };
};
